/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react';
import {
  Backdrop,
  Box,
  Button,
  Checkbox,
  Fade,
  IconButton,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Modal,
  Select,
  Link,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import clsx from 'clsx';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import { Toast } from 'components/Toast';
import { getFriendlyErrorMessage } from 'utils/errorHelper';
import { TRANSLATE_TITLE } from 'queries/titleContent';
import { SITE_BY_DOMAIN } from 'queries/site';
import useStyles from './styles';
import { contentLanguages, IContentLanguages } from './contentLanguages';

interface ICreateContentTranslationProps {
  titleId: string;
  siteDomain: string;
}

const CreateContentTranslation: React.FC<ICreateContentTranslationProps> = ({
  titleId,
  siteDomain,
}) => {
  const classes = useStyles();

  const languages = contentLanguages
    .filter((lang) => lang.code !== 'EN')
    .map((language: IContentLanguages) => language.code);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedLanguages, setSelectedLanguages] = useState<Array<string>>(
    languages,
  );

  const handleOpenModal = () => {
    setIsOpen(true);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  const handleSelectChange = (event: React.ChangeEvent<any>) => {
    if (event.target.value) {
      setSelectedLanguages(event.target.value);
    }
  };

  const handleOnCompletedMutation = () => {
    Toast('Action executed with success!');
    handleCloseModal();
  };

  const handleOnErrorMutation = (error: ApolloError) => {
    Toast(getFriendlyErrorMessage(error), 'error');
  };

  const [translateTitle] = useMutation(TRANSLATE_TITLE, {
    onCompleted: handleOnCompletedMutation,
    onError: handleOnErrorMutation,
  });

  const [loadSiteDomain, { data, loading }] = useLazyQuery(SITE_BY_DOMAIN, {
    variables: {
      domain: siteDomain,
    },
  });

  const createTranslation = () => {
    if (selectedLanguages.length) {
      translateTitle({
        variables: {
          id: titleId,
          codes: selectedLanguages,
        },
      });
    }
  };

  const renderContentTitlesNamesInSelectLabel = (selectedValues: any) => {
    const mappedSelectedLanguages = selectedValues.map(
      (languageCode: string) => {
        return (
          contentLanguages.find(
            (language: any) => language.code === languageCode,
          )?.language || ''
        );
      },
    );
    return mappedSelectedLanguages.join(', ');
  };

  const handleSelectAll = () => {
    setSelectedLanguages(languages);
  };

  const handleUselectAll = () => {
    setSelectedLanguages([]);
  };

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      loadSiteDomain();
    }

    return () => (mounted = false as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data) {
      const { languages } = data.siteByDomain;

      if (languages?.length) {
        setSelectedLanguages(languages);
      }
    }
  }, [data]);

  if (loading) return <></>;

  return (
    <>
      <Button
        variant="contained"
        onClick={handleOpenModal}
        style={{ backgroundColor: '#4B8BF5', color: '#fff' }}
      >
        Create translation
      </Button>

      {isOpen && (
        <Modal
          open={isOpen}
          onClose={handleCloseModal}
          aria-labelledby="create-content-translation-modal"
          aria-describedby="create-content-translation-modal-description"
          closeAfterTransition
          className={classes.modal}
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500,
            className: classes.backdrop,
          }}
        >
          <Fade in={isOpen}>
            <Box className={classes.modalPaper}>
              <Box className={classes.modalHeader}>
                <Box
                  className={clsx(
                    classes.modalHeaderSides,
                    classes.modalHeaderClose,
                  )}
                >
                  <IconButton onClick={handleCloseModal}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  component="div"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <InputLabel
                    className={classes.inputLabel}
                    style={{ marginTop: 0 }}
                  >
                    Select Languages
                  </InputLabel>

                  <Box component="div" style={{ display: 'flex' }}>
                    <Link onClick={handleSelectAll}>Select All</Link>
                    &nbsp;&nbsp;/&nbsp;&nbsp;
                    <Link onClick={handleUselectAll}>Unselect All</Link>
                  </Box>
                </Box>
                <Select
                  fullWidth
                  labelId="languages"
                  id="languages-select"
                  multiple
                  value={selectedLanguages}
                  onChange={(ev) => handleSelectChange(ev)}
                  input={<Input />}
                  renderValue={(selected) => {
                    return renderContentTitlesNamesInSelectLabel(selected);
                  }}
                  MenuProps={classes.menuProps as any}
                >
                  {contentLanguages.map((language: any) => (
                    <MenuItem key={language.code} value={language.code}>
                      <Checkbox
                        checked={selectedLanguages.indexOf(language.code) > -1}
                      />
                      <ListItemText primary={language.language} />
                    </MenuItem>
                  ))}
                </Select>

                <Button
                  style={{
                    backgroundColor: '#4B8BF5',
                    color: '#fff',
                    marginTop: '16px',
                  }}
                  onClick={createTranslation}
                >
                  Create translation
                </Button>
              </div>
            </Box>
          </Fade>
        </Modal>
      )}
    </>
  );
};

export default CreateContentTranslation;
